import { FunctionComponent, RefObject, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction'
import styles from './calendarMobile.module.scss'
import { EventClickArg, EventContentArg, MoreLinkContentArg } from '@fullcalendar/core'
import moment, { Moment } from 'moment-timezone'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import DatePickerDialog from '../../Dialog/datePicker/datePickerDialog.tsx'
import { CalendarView, calendarViews } from '../../utils/calendarParameters.ts'
import {
    compareEventsByType,
    compareEventsForParents,
    renderEventContent,
    renderEventGlobalRate,
    renderMoreLinkContent,
    stringToCalendarView
} from '../../utils/calendarEvents.tsx'
import { Daycare } from '@/types/Daycare.ts'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon.tsx'
import { Matcher } from 'react-day-picker'
import { getDaycareWeekClosingDay, getNextOpeningDate } from '@/modules/helper/daycareHelper.ts'
import { VerboseFormattingArg } from '@fullcalendar/core/internal'
import { EventCalendar } from '@/types/Calendar.ts'
import MobileAgendaView from './agendaView/mobileAgendaView.tsx'
import allLocales from '@fullcalendar/core/locales-all'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import ShowMoreLinkDialog from '../../Dialog/moreLinkDialog/showMoreLinkDialog.tsx'
import GlobalPlanningNotConfigured from '../components/GlobalPlanningNotConfigured.tsx'
import { getDraftingRoute, useMoreLinkDialog, useRemoveFcPopover } from '../hooks/useCalendarNavigation.ts'
import { useCalendarControls } from '../hooks/useCalendarControls.ts'
import { filterMoreLinkEvents, mapEventsWithClassName } from '../utils/calendarHelpers.ts'
import { FetchPlanningDataProps } from '../../hooks/usePlanningSlots.ts'

interface CalendarMobileProps {
    currentView: string
    setCurrentView: (view: string) => void
    fetchData: (props: FetchPlanningDataProps) => void
    isParent?: boolean
    calendarEvents: EventCalendar[]
    currentDaycare: Daycare
    hiddenDays: number[]
    handleSelect: (info: DateClickArg) => void
    handleEventClickArg: (info: EventClickArg) => void
    handleEventClick: (info: EventCalendar) => void
    isGlobalPlanning: boolean
}

const CalendarMobile: FunctionComponent<CalendarMobileProps> = ({
    currentView,
    setCurrentView,
    fetchData,
    isParent = false,
    calendarEvents,
    currentDaycare,
    hiddenDays,
    handleSelect,
    handleEventClickArg,
    handleEventClick,
    isGlobalPlanning
}: CalendarMobileProps) => {
    const { t } = useTranslation()
    const navigate: NavigateFunction = useNavigate()
    const calendarRef: RefObject<FullCalendar | null> = useRef(null)

    const [isDatePickerDialogOpen, setDatePickerDialogOpen] = useState(false)
    const [events, setEvents] = useState<EventCalendar[]>()
    const [disabledDay, setDisabledDay] = useState<Matcher[]>([])

    useRemoveFcPopover()

    const {
        handlePrev,
        handleNext,
        setNewCalendarTitle,
        calendarTitle,
        secondCalendarTitle,
        selectedDate,
        setSelectedDate
    } = useCalendarControls({
        calendarRef,
        currentView,
        setCurrentView,
        fetchData,
        isGlobalPlanning,
        isMobile: true,
        initialSelectedDate: getNextOpeningDate(currentDaycare, moment())
    })

    const {
        openMoreLinkDialog,
        setOpenMoreLinkDialog,
        moreLinkEvents,
        setMoreLinkEvents,
        moreLinkDate,
        handleMoreLinkClick
    } = useMoreLinkDialog()

    function formatAgendaDay(arg: VerboseFormattingArg): string {
        const date: Date = moment(arg.date).toDate()

        return format(date, 'EEEE dd MMMM yyyy', {
            locale: getLocalFromLanguage()
        })
    }

    useEffect(() => {
        setEvents(mapEventsWithClassName(calendarEvents, styles.EventCell))

        if (moreLinkEvents && moreLinkEvents.length > 0 && calendarEvents.length > 0) {
            setMoreLinkEvents(filterMoreLinkEvents(calendarEvents, moreLinkDate, styles.EventCell))
        }
    }, [calendarEvents])

    useEffect(() => {
        if (currentView !== CalendarView.Agenda && calendarRef.current) {
            const start: Moment = moment(calendarRef.current.getApi().view.activeStart)
            const end: Moment = moment(calendarRef.current.getApi().view.activeEnd).subtract(1, 'd')

            fetchData({ fromDate: start, toDate: end, isGlobalPlanning })
        }

        setDisabledDay([
            {
                dayOfWeek: [...getDaycareWeekClosingDay(currentDaycare).map((day: number) => day)]
            }
        ])
    }, [currentDaycare, isGlobalPlanning])

    const isGlobalPlanningNotConfigured: boolean =
        isGlobalPlanning && (currentDaycare.sectionsGroups === undefined || currentDaycare.sectionsGroups.length === 0)

    return (
        <div className={styles.CalendarHandler}>
            {isGlobalPlanningNotConfigured ? (
                <GlobalPlanningNotConfigured daycareId={currentDaycare.id} />
            ) : currentView === CalendarView.Agenda ? (
                <MobileAgendaView
                    currentDaycare={currentDaycare}
                    setSelectedDateForOtherView={setSelectedDate}
                    isParent={isParent}
                    disabledDay={disabledDay}
                    calendarEvents={calendarEvents}
                    fetchData={fetchData}
                    handleEventClick={handleEventClick}
                    isGlobalPlanning={isGlobalPlanning}
                />
            ) : (
                <div className={styles.CalendarAndHeader}>
                    <div className={styles.Header}>
                        <ButtonIcon
                            onClick={handlePrev}
                            icon={<ArrowBack />}
                            parent={isParent}
                            className={isParent ? styles.ButtonParent : styles.ButtonDaycare}
                        />
                        <div className={styles.HeaderTitle}>
                            <a onClick={() => setDatePickerDialogOpen(true)}>
                                <p className={styles.Title}>{calendarTitle} </p>
                                {secondCalendarTitle && <p className={styles.Title}>{secondCalendarTitle}</p>}
                            </a>
                        </div>
                        <ButtonIcon
                            onClick={handleNext}
                            icon={<ArrowForward />}
                            parent={isParent}
                            className={isParent ? styles.ButtonParent : styles.ButtonDaycare}
                        />
                    </div>
                    <div className={styles.Calendar}>
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            headerToolbar={false}
                            selectable={true}
                            datesSet={setNewCalendarTitle}
                            initialView={currentView}
                            views={calendarViews}
                            dateClick={handleSelect}
                            events={events}
                            dayCellClassNames={styles.Cell}
                            dayHeaderClassNames={`${isParent ? styles.HeaderCellParent : styles.HeaderCellDaycare}`}
                            height={'100%'}
                            locales={allLocales}
                            locale={getLocalFromLanguage()}
                            hiddenDays={hiddenDays}
                            eventClick={(e: EventClickArg) =>
                                isGlobalPlanning
                                    ? navigate(
                                          getDraftingRoute(
                                              e.event.extendedProps.sectionGroupId,
                                              moment(e.event.start).format('DD-MM-YYYY')
                                          )
                                      )
                                    : handleEventClickArg(e)
                            }
                            ref={calendarRef}
                            eventContent={(eventInfo: EventContentArg) =>
                                isGlobalPlanning
                                    ? renderEventGlobalRate(eventInfo, stringToCalendarView(currentView))
                                    : renderEventContent(eventInfo, stringToCalendarView(currentView))
                            }
                            moreLinkClassNames={`${isParent ? styles.MoreLinkParent : styles.MoreLinkDaycare}`}
                            noEventsClassNames={`${isParent ? styles.NoEventsParent : styles.NoEventsDaycare}`}
                            displayEventTime={false}
                            moreLinkContent={(info: MoreLinkContentArg) => renderMoreLinkContent(info, true, t)}
                            allDayClassNames={styles.AllDay}
                            eventOrder={isParent ? compareEventsForParents : compareEventsByType}
                            listDaySideFormat={false}
                            listDayFormat={formatAgendaDay}
                            moreLinkClick={handleMoreLinkClick}
                        />
                    </div>
                </div>
            )}
            {isDatePickerDialogOpen && (
                <DatePickerDialog
                    open={isDatePickerDialogOpen}
                    handleClose={() => setDatePickerDialogOpen(false)}
                    selectedDate={selectedDate}
                    handleDateChange={setSelectedDate}
                    currentDaycare={currentDaycare}
                />
            )}
            {openMoreLinkDialog && (
                <ShowMoreLinkDialog
                    open={openMoreLinkDialog}
                    handleClose={() => setOpenMoreLinkDialog(false)}
                    events={moreLinkEvents}
                    date={moreLinkDate}
                    isParent={isParent}
                    handleEventClick={handleEventClick}
                    isGlobalPlanning={isGlobalPlanning}
                />
            )}
        </div>
    )
}

export default CalendarMobile
